import { Injectable, OnDestroy, signal } from "@angular/core";
import { Subject } from "rxjs";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";

interface GfxStage {
  stop?: (ms?: number) => void;
  seek?: (ms: number) => void;
  update?: () => void;
}

const BASE_STAGE_SIZE_BY_ASPECT_RATIO: Record<AspectRatio, { w: number; h: number }> = {
  [AspectRatio.Aspect16x9]: { w: 1920, h: 1080 },
  [AspectRatio.Aspect9x16]: { w: 1080, h: 1920 },
  [AspectRatio.Aspect1x1]: { w: 1080, h: 1080 },
  [AspectRatio.Aspect4x3]: { w: 1440, h: 1080 },
};

/**
 * Low-level bridge between the host app and each GFX iframe.
 *
 * All iframe-related bookkeeping is keyed by `timelineGfxId` (a unique id per
 * timeline GFX segment, i.e. `ClippingStudioGraphicEvent.id`), so that multiple
 * timeline segments referencing the same backend `gfxInstanceId` each get their
 * own iframe/snapshot pipeline.
 *
 * Note: the `payload.instanceId` emitted by the GFX bridge script from inside
 * the iframe (used for `gfx-loaded` and `gfx-session-unauthorized`) is ignored
 * for correlation; instead we map the source `Window` to the owning
 * `timelineGfxId` via a `WeakMap` registered at iframe creation time.
 */
@Injectable()
export class ClippingStudioGfxBridgeService implements OnDestroy {
  private iframes = new Map<string, HTMLIFrameElement>();
  private offscreenCanvases = new Map<string, HTMLCanvasElement>();
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private initializedTimelineIds = new Set<string>();
  private currentAspectRatio: AspectRatio = AspectRatio.Aspect16x9;

  /**
   * Maps an iframe's `contentWindow` to the owning `timelineGfxId`. Used for
   * O(1) correlation of postMessage events (`gfx-loaded`,
   * `gfx-session-unauthorized`) to the correct segment. Entries are removed in
   * `destroyIframe`; the `WeakMap` also auto-cleans when iframes are GC'd.
   */
  private readonly windowToTimelineId = new WeakMap<Window, string>();

  /** Tracks which timeline GFX segments have received a 'gfx-loaded' message. */
  readonly loadedTimelineIds = signal(new Set<string>());

  /** Per-segment SQZ flag set from the `gfx-loaded` payload. Cleared on iframe destroy. */
  private readonly sqzByTimelineId = new Map<string, boolean>();

  /** Emits the `timelineGfxId` when the bridge reports a serve-session 401. */
  readonly sessionExpired$ = new Subject<string>();

  constructor() {
    this.messageHandler = (event: MessageEvent) => this.handleMessage(event);
    window.addEventListener("message", this.messageHandler);
  }

  ngOnDestroy(): void {
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
    this.destroyAll();
    this.sessionExpired$.complete();
  }

  isLoaded(timelineGfxId: string): boolean {
    return this.loadedTimelineIds().has(timelineGfxId);
  }

  isSqz(timelineGfxId: string): boolean {
    return this.sqzByTimelineId.get(timelineGfxId) === true;
  }

  setCurrentAspectRatio(option: AspectRatio): void {
    if (this.currentAspectRatio === option) return;
    this.currentAspectRatio = option;
  }

  getCurrentBaseStageSize(): { w: number; h: number } {
    return this.getBaseStageSize();
  }

  private getBaseStageSize(): { w: number; h: number } {
    return BASE_STAGE_SIZE_BY_ASPECT_RATIO[this.currentAspectRatio];
  }

  /**
   * Creates a hidden iframe for the given timeline GFX segment.
   * @param timelineGfxId Unique timeline segment id (`ClippingStudioGraphicEvent.id`).
   * @param serveUrl Full serve URL including ?session=... and any &TEXT1=... params.
   * @param container DOM element to append the iframe to.
   * @param opts Optional sizing options (scale is 1.0 = 100%).
   */
  createIframe(
    timelineGfxId: string,
    serveUrl: string,
    container: HTMLElement,
    opts?: { scale?: number },
  ): void {
    this.destroyIframe(timelineGfxId);

    const iframe = document.createElement("iframe");
    iframe.setAttribute("data-gfx-timeline-id", timelineGfxId);
    iframe.src = serveUrl;

    const scale = opts?.scale;
    const normalizedScale =
      scale != null && Number.isFinite(scale) && scale > 0 ? scale : 1;
    const base = this.getBaseStageSize();
    const w = Math.max(1, Math.round(base.w * normalizedScale));
    const h = Math.max(1, Math.round(base.h * normalizedScale));
    iframe.style.cssText =
      `position:absolute;width:${w}px;height:${h}px;opacity:0;pointer-events:none;z-index:-1;border:none;`;
    iframe.setAttribute("sandbox", "allow-scripts allow-same-origin");

    container.appendChild(iframe);
    this.iframes.set(timelineGfxId, iframe);

    // Register contentWindow → timelineGfxId mapping for O(1) postMessage correlation.
    // contentWindow may be briefly null on detached iframes, so re-check after attachment.
    const win = iframe.contentWindow;
    if (win) {
      this.windowToTimelineId.set(win, timelineGfxId);
    }
  }

  /**
   * Resizes an existing iframe's CSS width/height for the given timeline segment.
   * No-op if the iframe does not exist.
   *
   * Scale is a coefficient (1.0 = 100%) applied to the current "base stage" size.
   * The base stage size depends on the current `AspectRatio` option
   * (see `BASE_STAGE_SIZE_BY_ASPECT_RATIO`).
   */
  setIframeScale(timelineGfxId: string, scale: number): void {
    const iframe = this.iframes.get(timelineGfxId);
    if (!iframe) return;
    const normalizedScale =
      scale != null && Number.isFinite(scale) && scale > 0 ? scale : 1;
    const base = this.getBaseStageSize();
    const w = Math.max(1, Math.round(base.w * normalizedScale));
    const h = Math.max(1, Math.round(base.h * normalizedScale));
    iframe.style.width = `${w}px`;
    iframe.style.height = `${h}px`;
  }

  destroyIframe(timelineGfxId: string): void {
    const iframe = this.iframes.get(timelineGfxId);
    if (iframe) {
      // WeakMap entry is auto-cleaned when the Window is GC'd, but we don't need
      // to remove it explicitly because the Window object is not re-used.
      iframe.remove();
      this.iframes.delete(timelineGfxId);
    }
    this.offscreenCanvases.delete(timelineGfxId);
    this.initializedTimelineIds.delete(timelineGfxId);
    this.sqzByTimelineId.delete(timelineGfxId);

    const loaded = new Set(this.loadedTimelineIds());
    if (loaded.delete(timelineGfxId)) {
      this.loadedTimelineIds.set(loaded);
    }
  }

  destroyAll(): void {
    for (const timelineGfxId of this.iframes.keys()) {
      this.destroyIframe(timelineGfxId);
    }
  }

  /**
   * Captures the current frame of the GFX iframe's internal canvas.
   * Returns an offscreen canvas with the snapshot drawn, or null if not available.
   */
  captureSnapshot(timelineGfxId: string): HTMLCanvasElement | null {
    const iframe = this.iframes.get(timelineGfxId);
    if (!iframe) return null;

    let innerCanvas: HTMLCanvasElement | null = null;
    try {
      innerCanvas = iframe.contentDocument?.getElementById("canvas") as HTMLCanvasElement | null;
    } catch {
      return null;
    }
    if (!innerCanvas || !innerCanvas.width || !innerCanvas.height) return null;

    let offscreen = this.offscreenCanvases.get(timelineGfxId);
    if (
      !offscreen ||
      offscreen.width !== innerCanvas.width ||
      offscreen.height !== innerCanvas.height
    ) {
      offscreen = document.createElement("canvas");
      offscreen.width = innerCanvas.width;
      offscreen.height = innerCanvas.height;
      this.offscreenCanvases.set(timelineGfxId, offscreen);
    }

    const ctx = offscreen.getContext("2d");
    if (!ctx) return null;
    ctx.clearRect(0, 0, offscreen.width, offscreen.height);
    ctx.drawImage(innerCanvas, 0, 0);
    return offscreen;
  }

  // ── Readiness checks ────────────────────────────────────────────────

  /**
   * Returns true when the Animate `handleComplete` callback has reached
   * the point where it assigns the `StartAnimation` function.
   * This is the reliable signal that LoadQueue finished and the scene
   * objects (`stage`, `exportRoot`) are fully wired — unlike checking
   * `stage && exportRoot` alone, which becomes truthy before `StartAnimation`
   * is assigned and therefore does not prove `handleComplete` completed.
   */
  isStartAnimationReady(timelineGfxId: string): boolean {
    const win = this.getIframeWindow(timelineGfxId);
    if (!win) return false;
    try {
      return typeof win["StartAnimation"] === "function";
    } catch {
      return false;
    }
  }

  /**
   * Polls until `isStartAnimationReady` is true or the absolute `deadlineMs`
   * timestamp is reached. Used as phase B of the two-phase load wait
   * (after `gfx-loaded` has already been received).
   */
  waitForAnimateReady(timelineGfxId: string, deadlineMs: number): Promise<void> {
    if (this.isStartAnimationReady(timelineGfxId)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const pollInterval = 200;
      const timer = setInterval(() => {
        if (this.isStartAnimationReady(timelineGfxId)) {
          clearInterval(timer);
          resolve();
          return;
        }
        if (Date.now() >= deadlineMs) {
          clearInterval(timer);
          reject(new Error(`GFX segment ${timelineGfxId} Animate ready timeout`));
        }
      }, pollInterval);
    });
  }

  /**
   * One-time bootstrap after the two-phase load (gfx-loaded + StartAnimation ready).
   * Calls `StartAnimation()` to wire `exportRoot` onto the `Stage` and attach the
   * CreateJS ticker, then immediately stops to freeze the first frame and disable
   * the ticker. After this point the host drives frames exclusively via `seekTo`;
   * the internal ticker is never re-enabled.
   * Guarded by `initializedTimelineIds` — safe to call multiple times.
   */
  bootstrapAfterLoad(timelineGfxId: string): void {
    if (this.initializedTimelineIds.has(timelineGfxId)) return;

    const win = this.getIframeWindow(timelineGfxId);
    if (!win) return;

    const start = win["StartAnimation"];
    if (typeof start === "function") {
      try {
        (start as () => void)();
      } catch {
        // template init may throw
      }
    }

    this.initializedTimelineIds.add(timelineGfxId);
    this.stop(timelineGfxId);
  }

  // ── Public API (called by manager effect) ─────────────────────────────

  /**
   * Ensures the iframe scene is bootstrapped before seekTo.
   * Called on every effect tick for visible GFX but is a no-op after the
   * first successful bootstrap (guarded by `initializedTimelineIds`).
   *
   * Unlike `bootstrapAfterLoad`, this is a fallback path: if bootstrap
   * already ran during the load flow, this returns immediately. Otherwise
   * it attempts `StartAnimation()` if the function is available.
   */
  ensureInitialized(timelineGfxId: string): void {
    if (this.initializedTimelineIds.has(timelineGfxId)) return;
    const win = this.getIframeWindow(timelineGfxId);
    if (!win) return;

    const start = win["StartAnimation"];
    if (typeof start === "function") {
      try {
        (start as () => void)();
      } catch {
        // template init may throw
      }
      this.initializedTimelineIds.add(timelineGfxId);
    }
  }

  /**
   * Seek the template to a specific local time (ms) and freeze on that frame.
   * Used by the host-driven playback model: `ClippingStudioGfxManagerService.seekVisibleForPreview`
   * (from the preview overlay draw path) calls `seekTo` for each visible GFX before snapshot.
   * Prefer stage.seek + stage.stop + stage.update; fallback: stop ticker only (logged).
   */
  seekTo(timelineGfxId: string, localMs: number): void {
    const win = this.getIframeWindow(timelineGfxId);
    if (!win) return;
    const stage = win["stage"] as GfxStage | undefined;

    if (stage && typeof stage.seek === "function") {
      try {
        stage.seek(localMs);
      } catch { /* ignore */ }
      if (typeof stage.stop === "function") {
        try { stage.stop(); } catch { /* ignore */ }
      }
      if (typeof stage.update === "function") {
        try { stage.update(); } catch { /* ignore */ }
      }
      return;
    }

    this.invokeStopFallback(timelineGfxId);
  }

  /**
   * Pause playback. Prefer stage.stop; fallback: detach CreateJS ticker (logged with console.warn).
   */
  stop(timelineGfxId: string): void {
    const win = this.getIframeWindow(timelineGfxId);
    if (!win) return;
    const stage = win["stage"] as GfxStage | undefined;

    if (stage && typeof stage.stop === "function") {
      try { stage.stop(); } catch { /* ignore */ }
      return;
    }

    this.invokeStopFallback(timelineGfxId);
  }

  // ── Internal ─────────────────────────────────────────────────────────

  private handleMessage(event: MessageEvent): void {
    const msg = event.data;
    if (!msg || msg.source !== "gfx-bridge") return;

    switch (msg.type) {
      case "gfx-loaded":
        this.onGfxLoaded(event);
        break;
      case "gfx-session-unauthorized":
        this.onGfxSessionUnauthorized(event);
        break;
    }
  }

  /**
   * Resolves the `timelineGfxId` owning the `event.source` window, preferring
   * the WeakMap lookup (O(1)) and falling back to an `iframes`-scan for
   * resilience (e.g. if the iframe navigated and the Window reference changed).
   */
  private resolveTimelineIdFromSource(sourceWindow: Window): string | null {
    const viaMap = this.windowToTimelineId.get(sourceWindow);
    if (viaMap != null) return viaMap;

    for (const [id, iframe] of this.iframes) {
      if (iframe.contentWindow === sourceWindow) {
        // Lazily repair WeakMap so subsequent events are O(1).
        this.windowToTimelineId.set(sourceWindow, id);
        return id;
      }
    }
    return null;
  }

  private onGfxLoaded(event: MessageEvent): void {
    const sourceWindow = event.source as Window | null;
    if (!sourceWindow) return;

    const timelineGfxId = this.resolveTimelineIdFromSource(sourceWindow);
    if (timelineGfxId == null) {
      return;
    }

    const payload = event.data?.payload;
    this.sqzByTimelineId.set(timelineGfxId, payload?.sqz === true);

    const loaded = new Set(this.loadedTimelineIds());
    loaded.add(timelineGfxId);
    this.loadedTimelineIds.set(loaded);
  }

  private onGfxSessionUnauthorized(event: MessageEvent): void {
    const sourceWindow = event.source as Window | null;
    if (!sourceWindow) return;

    const timelineGfxId = this.resolveTimelineIdFromSource(sourceWindow);
    if (timelineGfxId == null) {
      return;
    }

    this.sessionExpired$.next(timelineGfxId);
  }

  /**
   * Fallback stop for templates without stage.stop: detach ticker listener to freeze animation.
   * exportRoot stays on the stage so captureSnapshot can still read the last rendered frame.
   */
  private invokeStopFallback(timelineGfxId: string): void {
    const win = this.getIframeWindow(timelineGfxId);
    if (!win) return;

    try {
      const createjs = win["createjs"] as
        | { Ticker?: { removeEventListener: (type: string, listener: unknown) => void } }
        | undefined;
      const stage = win["stage"] as GfxStage | undefined;
      if (createjs?.Ticker && stage) {
        console.warn(
          `[ClippingStudioGfxBridge] stop fallback (Ticker detach) for segment ${timelineGfxId} — no stage.stop/seek`,
        );
        createjs.Ticker.removeEventListener("tick", stage);
        if (typeof stage.update === "function") {
          try { stage.update(); } catch { /* ignore */ }
        }
      }
    } catch {
      // cross-origin or non-Animate template
    }
  }

  private getIframeWindow(timelineGfxId: string): Record<string, unknown> | null {
    const iframe = this.iframes.get(timelineGfxId);
    if (!iframe) return null;
    try {
      return iframe.contentWindow as unknown as Record<string, unknown>;
    } catch {
      return null;
    }
  }
}
